import type { AmbienceBank, AmbienceBankStats, ReadyBed } from "./ambience-bank.ts";
import { BED_IDS, synthCover, type AmbienceInput, type AmbienceMix, type BedId, type SynthCover, type Weights } from "./ambience-model.ts";

/** Do pedido de um leito ao som: já decodificado, só com os bytes guardados ("morno") ou sem nada ("frio"). */
export interface BedLatency {
  bed: BedId;
  ms: number;
  kind: "decodificado" | "morno" | "frio";
}

export interface AmbienceStats extends AmbienceBankStats {
  /** A última mistura aplicada (entrada, alvos, pesos suavizados, pedidos e os que tocam). */
  input?: AmbienceInput;
  targets?: Weights;
  levels?: Weights;
  mountain?: number;
  requested: BedId[];
  playing: BedId[];
  /** Leitos soando de fato (com buffer e contexto) e o nível de cada um. */
  voices: BedId[];
  audible: Partial<Record<BedId, number>>;
  /** O quanto de cada camada sintetizada toca por baixo. */
  cover?: SynthCover;
  /** As últimas esperas do pedido ao som. */
  latency: BedLatency[];
}

interface Voice {
  src: AudioBufferSourceNode;
  gain: GainNode;
  bed: ReadyBed;
}

const MAX_LATENCIES = 20;

/**
 * Toca os leitos de ambiente (F46) que a mistura pede: prende no banco os que tocam (no máximo 3),
 * pré-carrega os sugeridos e, quando o leito chega decodificado, toca em loop entre `loopStart` e
 * `loopEnd` (de um ponto sorteado do loop), com ganho √peso. Devolve o que soa de fato: a síntese
 * só abaixa sob um leito que está tocando.
 */
export class AmbiencePlayer {
  private ctx: BaseAudioContext | undefined;
  private out: AudioNode | undefined;
  private readonly voices = new Map<BedId, Voice>();
  private readonly pinned = new Set<BedId>();
  private readonly pending = new Map<BedId, { at: number; kind: BedLatency["kind"] }>();
  private readonly latency: BedLatency[] = [];
  /** Como parar cada prévia da bancada que toca (o fim da cena para todas). */
  private readonly previews = new Set<() => void>();
  /** Leitos em fade esperando para soltar, e se o fim da cena pediu para tirar o PCM quando acabarem. */
  private releasing = 0;
  private trimPending = false;
  private last: { mix: AmbienceMix; audible: Partial<Record<BedId, number>>; cover: SynthCover } | undefined;
  readonly bank: AmbienceBank;
  private readonly now: () => number;

  constructor(bank: AmbienceBank, now: () => number = () => performance.now()) {
    this.bank = bank;
    this.now = now;
  }

  /** O contexto nasceu (primeiro gesto): os leitos vão para `out` (o ganho `leitos`). */
  attach(ctx: BaseAudioContext, out: AudioNode): void {
    this.ctx = ctx;
    this.out = out;
  }

  apply(mix: AmbienceMix): { audible: Partial<Record<BedId, number>>; cover: SynthCover } {
    // Cena nova antes do fim dos fades da anterior: o PCM fica para ela.
    this.trimPending = false;
    for (const id of BED_IDS) {
      const want = mix.playing.includes(id);
      if (want && !this.pinned.has(id)) {
        this.pinned.add(id);
        const state = this.bank.state(id);
        this.pending.set(id, { at: this.now(), kind: state === "decodificado" ? "decodificado" : state === "comprimido" ? "morno" : "frio" });
        this.bank.pin(id);
      } else if (!want && this.pinned.has(id)) {
        this.pinned.delete(id);
        this.pending.delete(id);
        this.release(id, 1);
      }
    }
    for (const id of mix.prefetch) this.bank.prefetch(id);
    const audible: Partial<Record<BedId, number>> = {};
    for (const id of mix.playing) {
      let voice = this.voices.get(id);
      if (!voice) {
        const bed = this.bank.get(id);
        if (bed) voice = this.startVoice(id, bed);
      }
      if (!voice || !this.ctx) continue;
      const level = mix.levels[id];
      voice.gain.gain.setTargetAtTime(Math.sqrt(level) * voice.bed.gain, this.ctx.currentTime, 0.1);
      audible[id] = level;
    }
    const cover = synthCover(mix.levels, audible, mix.mountain);
    this.last = { mix, audible, cover };
    return { audible, cover };
  }

  /** Fonte em loop entre os pontos do leito, de `offset` (s), com o ganho dado. */
  private source(ctx: BaseAudioContext, out: AudioNode, bed: ReadyBed, offset: number, level: number): { src: AudioBufferSourceNode; gain: GainNode } {
    const src = ctx.createBufferSource();
    src.buffer = bed.buffer;
    src.loop = true;
    src.loopStart = bed.loopStart;
    src.loopEnd = bed.loopEnd;
    const gain = ctx.createGain();
    gain.gain.value = level;
    src.connect(gain).connect(out);
    src.onended = () => gain.disconnect();
    src.start(ctx.currentTime, offset);
    return { src, gain };
  }

  private startVoice(id: BedId, bed: ReadyBed): Voice | undefined {
    const ctx = this.ctx;
    if (!ctx || !this.out) return undefined;
    try {
      // Um ponto sorteado do loop: duas visitas à praia não começam no mesmo trecho.
      const voice = { ...this.source(ctx, this.out, bed, bed.loopStart + Math.random() * (bed.loopEnd - bed.loopStart), 0), bed };
      this.voices.set(id, voice);
      const asked = this.pending.get(id);
      if (asked) {
        this.pending.delete(id);
        this.latency.push({ bed: id, ms: Math.round(this.now() - asked.at), kind: asked.kind });
        if (this.latency.length > MAX_LATENCIES) this.latency.shift();
      }
      return voice;
    } catch {
      return undefined;
    }
  }

  /**
   * Para o leito (com fade) e o solta no banco só depois do fade: enquanto a voz soa, o buffer dela
   * continua contando no LRU (senão, numa troca, seriam 4 leitos decodificados por um segundo).
   */
  private release(id: BedId, fade: number): void {
    if (!this.stopVoice(id, fade)) {
      this.bank.unpin(id);
      return;
    }
    this.releasing++;
    setTimeout(() => {
      this.releasing--;
      this.bank.unpin(id);
      if (this.trimPending && this.releasing === 0) this.trim();
    }, (fade + 0.2) * 1000);
  }

  private trim(): void {
    this.trimPending = false;
    this.bank.trim();
  }

  /** Para a voz do leito, se houver (devolve se havia). */
  private stopVoice(id: BedId, fade: number): boolean {
    const voice = this.voices.get(id);
    if (!voice || !this.ctx) return false;
    this.voices.delete(id);
    const t = this.ctx.currentTime;
    voice.gain.gain.cancelScheduledValues(t);
    voice.gain.gain.setTargetAtTime(0, t, fade / 3);
    try {
      voice.src.stop(t + fade + 0.1);
    } catch {
      // já parou
    }
    return true;
  }

  /**
   * Bancada (`?debug=sons`): um leito sozinho, no ganho dele, do começo do loop ou 2 s antes da
   * emenda (`seam`). Devolve como parar, ou nada (sem contexto ou sem o leito).
   */
  async preview(id: BedId, seam = false): Promise<(() => void) | undefined> {
    this.bank.pin(id);
    const bed = await this.bank.whenReady(id);
    const ctx = this.ctx;
    if (!bed || !ctx || !this.out) {
      this.bank.unpin(id);
      return undefined;
    }
    const { src, gain } = this.source(ctx, this.out, bed, seam ? bed.loopEnd - 2 : bed.loopStart, bed.gain);
    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      this.previews.delete(stop);
      gain.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
      try {
        src.stop(ctx.currentTime + 0.3);
      } catch {
        // já parou
      }
      this.bank.unpin(id);
    };
    this.previews.add(stop);
    return stop;
  }

  /**
   * Fim da cena: para os leitos e as prévias, solta todos e, quando o último fade acaba (inclusive
   * o de um leito que saiu pouco antes), tira o PCM da memória (os bytes ficam). Uma decodificação
   * ainda em andamento é descartada pelo banco quando chega, porque ninguém mais prende o leito.
   */
  stop(): void {
    for (const stop of [...this.previews]) stop();
    for (const id of this.pinned) this.release(id, 0.3);
    this.pinned.clear();
    this.pending.clear();
    this.last = undefined;
    this.trimPending = true;
    if (this.releasing === 0) this.trim();
  }

  stats(): AmbienceStats {
    const m = this.last?.mix;
    return {
      ...this.bank.stats(),
      ...(m && { input: m.input, targets: m.targets, levels: m.levels, mountain: m.mountain }),
      requested: m?.requested ?? [],
      playing: m?.playing ?? [],
      voices: [...this.voices.keys()],
      audible: { ...this.last?.audible },
      ...(this.last && { cover: this.last.cover }),
      latency: [...this.latency],
    };
  }
}
